import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, Button, Card, Form, Spinner, Table } from 'react-bootstrap'
import { Plus, Save, Trash2 } from 'lucide-react'
import {
  fetchVariantMatrix,
  saveVariantMatrix,
  type ThermalConductivityRuleDto,
  type VariantMatrixDto,
} from '../services/api'

type VariantRowDraft = {
  clientKey: string
  id?: string
  thicknessMm: string
  thermalConductivity: string
  pricePerM2: string
  boardsPerPack: string
  boardsPerPallet: string
  weightKgPerM2: string
  m2PerPack: string
}

const DEFAULT_RULES: ThermalConductivityRuleDto[] = [
  { minThicknessMm: 25, maxThicknessMm: 44, value: '0.022', unit: 'W/mK' },
  { minThicknessMm: 45, maxThicknessMm: null, value: '0.021', unit: 'W/mK' },
]

const K5_THICKNESSES_MM = [25, 50, 60, 70, 80, 90, 100]

function newClientKey() {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function resolveLambda(thickness: number, rules: ThermalConductivityRuleDto[]): string {
  for (const rule of rules) {
    if (thickness < rule.minThicknessMm) continue
    if (rule.maxThicknessMm != null && thickness > rule.maxThicknessMm) continue
    return rule.value
  }
  return ''
}

function toDraftRow(row: VariantMatrixDto['variants'][number]): VariantRowDraft {
  return {
    clientKey: row.id ?? newClientKey(),
    id: row.id,
    thicknessMm: row.thicknessMm != null ? String(row.thicknessMm) : '',
    thermalConductivity: row.thermalConductivity ?? '',
    pricePerM2: row.pricePerM2 != null ? String(row.pricePerM2) : '',
    boardsPerPack: row.boardsPerPack != null ? String(row.boardsPerPack) : '',
    boardsPerPallet: row.boardsPerPallet != null ? String(row.boardsPerPallet) : '',
    weightKgPerM2: row.weightKgPerM2 != null ? String(row.weightKgPerM2) : '',
    m2PerPack: row.m2PerPack != null ? String(row.m2PerPack) : '',
  }
}

function emptyRow(): VariantRowDraft {
  return {
    clientKey: newClientKey(),
    thicknessMm: '',
    thermalConductivity: '',
    pricePerM2: '',
    boardsPerPack: '',
    boardsPerPallet: '',
    weightKgPerM2: '',
    m2PerPack: '',
  }
}

function parseNum(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  return Number.isFinite(n) ? n : undefined
}

function parseIntOrUndef(value: string): number | undefined {
  const n = parseNum(value)
  if (n == null) return undefined
  return Math.trunc(n)
}

type Props = {
  productId: string
  onSaved?: () => void
}

export default function VariantMatrixEditor({ productId, onSaved }: Props) {
  const [rules, setRules] = useState<ThermalConductivityRuleDto[]>(DEFAULT_RULES)
  const [rows, setRows] = useState<VariantRowDraft[]>([])
  const [matrixMessage, setMatrixMessage] = useState<string | null>(null)

  const matrixQuery = useQuery({
    queryKey: ['variant-matrix', productId],
    queryFn: () => fetchVariantMatrix(productId),
  })

  useEffect(() => {
    if (!matrixQuery.data) return
    setRules(matrixQuery.data.conductivityRules?.length ? matrixQuery.data.conductivityRules : DEFAULT_RULES)
    setRows(matrixQuery.data.variants.length > 0 ? matrixQuery.data.variants.map(toDraftRow) : [])
  }, [matrixQuery.data])

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: VariantMatrixDto = {
        conductivityRules: rules,
        variants: rows.map(row => ({
          id: row.id,
          thicknessMm: parseNum(row.thicknessMm),
          thermalConductivity: row.thermalConductivity.trim() || undefined,
          pricePerM2: parseNum(row.pricePerM2),
          boardsPerPack: parseIntOrUndef(row.boardsPerPack),
          boardsPerPallet: parseIntOrUndef(row.boardsPerPallet),
          weightKgPerM2: parseNum(row.weightKgPerM2),
          m2PerPack: parseNum(row.m2PerPack),
        })),
      }
      return saveVariantMatrix(productId, payload)
    },
    onSuccess: async () => {
      setMatrixMessage('Variant matrix saved as Kingspan source of truth.')
      await matrixQuery.refetch()
      onSaved?.()
    },
    onError: (err: unknown) => {
      const axiosErr = err as { response?: { data?: string | { title?: string } }; message?: string }
      const data = axiosErr.response?.data
      setMatrixMessage(typeof data === 'string' ? data : (data as { title?: string })?.title ?? axiosErr.message ?? 'Save failed')
    },
  })

  const applyLambdaFromRules = (row: VariantRowDraft): VariantRowDraft => {
    const thickness = parseNum(row.thicknessMm)
    if (thickness == null) return row
    const lambda = resolveLambda(thickness, rules)
    return lambda ? { ...row, thermalConductivity: lambda } : row
  }

  const updateRow = (clientKey: string, patch: Partial<VariantRowDraft>) => {
    setRows(prev => prev.map(row => {
      if (row.clientKey !== clientKey) return row
      const next = { ...row, ...patch }
      if ('thicknessMm' in patch) return applyLambdaFromRules(next)
      return next
    }))
  }

  const addRow = () => setRows(prev => [...prev, emptyRow()])

  const addK5ThicknessRows = () => {
    setRows(prev => {
      const existing = new Set(prev.map(r => parseNum(r.thicknessMm)).filter((n): n is number => n != null))
      const additions = K5_THICKNESSES_MM.filter(t => !existing.has(t)).map(t => applyLambdaFromRules({
        ...emptyRow(),
        thicknessMm: String(t),
      }))
      return [...prev, ...additions]
    })
  }

  const removeRow = (clientKey: string) => {
    setRows(prev => prev.filter(r => r.clientKey !== clientKey))
  }

  const sortedPreview = useMemo(() => {
    return [...rows].sort((a, b) => (parseNum(a.thicknessMm) ?? 0) - (parseNum(b.thicknessMm) ?? 0))
  }, [rows])

  return (
    <Card className="mb-0 border variant-matrix-card">
      <Card.Body>
        <p className="small text-muted mb-2">
          Each row is a size variant (e.g. 25 mm, 50 mm). λ can follow thickness-band rules; override per row if needed.
        </p>

        {matrixQuery.isLoading && <div className="small text-muted">Loading variant matrix…</div>}

        {matrixQuery.isError && (
          <Alert variant="warning" className="py-2 small mb-2">
            Could not load variant matrix. You can still add rows and save.
          </Alert>
        )}

        <div className="mb-3">
          <div className="small fw-semibold mb-1">Thermal conductivity by thickness (λ)</div>
          <Table size="sm" bordered className="mb-0 variant-matrix-rules-table">
            <thead>
              <tr>
                <th>Min (mm)</th>
                <th>Max (mm)</th>
                <th>λ (W/mK)</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, index) => (
                <tr key={`rule-${index}`}>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      value={rule.minThicknessMm}
                      onChange={e => {
                        const minThicknessMm = Number(e.target.value)
                        setRules(prev => prev.map((r, i) => i === index ? { ...r, minThicknessMm } : r))
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      type="number"
                      placeholder="∞"
                      value={rule.maxThicknessMm ?? ''}
                      onChange={e => {
                        const raw = e.target.value.trim()
                        setRules(prev => prev.map((r, i) => i === index
                          ? { ...r, maxThicknessMm: raw ? Number(raw) : null }
                          : r))
                      }}
                    />
                  </td>
                  <td>
                    <Form.Control
                      size="sm"
                      value={rule.value}
                      onChange={e => {
                        const value = e.target.value
                        setRules(prev => prev.map((r, i) => i === index ? { ...r, value } : r))
                      }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Form.Text className="text-muted">
            When you change thickness on a variant row, λ is filled from these rules (you can override per row).
          </Form.Text>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-2">
          <Button size="sm" variant="outline-secondary" onClick={addRow}>
            <Plus size={14} className="me-1" /> Add row
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={addK5ThicknessRows}>
            Add standard thicknesses (25–100 mm)
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={rows.length === 0 || saveMutation.isPending}
            onClick={() => {
              setMatrixMessage(null)
              saveMutation.mutate()
            }}
          >
            {saveMutation.isPending ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Saving…
              </>
            ) : (
              <>
                <Save size={14} className="me-1" />
                Save variant matrix
              </>
            )}
          </Button>
        </div>

        {matrixMessage && (
          <Alert
            variant={matrixMessage.toLowerCase().includes('fail') ? 'danger' : 'success'}
            className="py-2 small mb-2"
          >
            {matrixMessage}
          </Alert>
        )}

        {rows.length === 0 ? (
          <Alert variant="secondary" className="py-2 small mb-0">
            No variants defined yet. Add rows for each nominal thickness (e.g. seven rows for K5: 25, 50, 60, 70, 80, 90, 100 mm)
            and enter price / pack data from the Kingspan datasheet.
          </Alert>
        ) : (
          <div className="variant-matrix-table-wrap">
            <Table size="sm" bordered hover className="mb-0 align-middle variant-matrix-table">
              <thead>
                <tr>
                  <th>Thickness (mm)</th>
                  <th>λ W/mK</th>
                  <th>Price £/m²</th>
                  <th>Boards / pack</th>
                  <th>Boards / pallet</th>
                  <th>Weight kg/m²</th>
                  <th>m² / pack</th>
                  <th style={{ width: 44 }} />
                </tr>
              </thead>
              <tbody>
                {sortedPreview.map(row => (
                  <tr key={row.clientKey}>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.thicknessMm}
                        onChange={e => updateRow(row.clientKey, { thicknessMm: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        value={row.thermalConductivity}
                        placeholder="Auto"
                        onChange={e => updateRow(row.clientKey, { thermalConductivity: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.pricePerM2}
                        onChange={e => updateRow(row.clientKey, { pricePerM2: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.boardsPerPack}
                        onChange={e => updateRow(row.clientKey, { boardsPerPack: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.boardsPerPallet}
                        onChange={e => updateRow(row.clientKey, { boardsPerPallet: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.weightKgPerM2}
                        onChange={e => updateRow(row.clientKey, { weightKgPerM2: e.target.value })}
                      />
                    </td>
                    <td>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.m2PerPack}
                        onChange={e => updateRow(row.clientKey, { m2PerPack: e.target.value })}
                      />
                    </td>
                    <td className="text-center">
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="p-1"
                        title="Remove row"
                        onClick={() => removeRow(row.clientKey)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
